// Product carousels: any list with [data-carousel] gets working prev/next buttons
// ([data-row-prev] / [data-row-next] in the same section). Lists that also have
// [data-autoscroll] move forward one card at a time on their own, pausing between
// steps. Touching, dragging, scrolling or using an arrow stops it for good; mouse hover pauses it.
const STEP_DELAY = 1700; // merchant 19 Oct: more movement (was 2500)
const RESUME_DELAY = 2000;
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

function setup(track) {
  if (track.dataset.carouselReady) return;
  track.dataset.carouselReady = 'true';

  const autoplay = track.hasAttribute('data-autoscroll');
  let timer = null;
  let resumeTimer = null;
  let held = false;
  let visible = false;

  const behavior = () => (reduceMotion.matches ? 'auto' : 'smooth');
  const isRtl = () => getComputedStyle(track).direction === 'rtl';

  const cardStep = () => {
    const first = track.children[0];
    if (!first) return track.clientWidth;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    return first.getBoundingClientRect().width + gap;
  };

  const maxScroll = () => track.scrollWidth - track.clientWidth;
  const position = () => Math.abs(track.scrollLeft);
  const sign = () => (isRtl() ? -1 : 1);

  const go = (dir) => {
    const max = maxScroll();
    if (max <= 4) return;
    if (dir > 0 && position() >= max - 4) {
      track.scrollTo({ left: 0, behavior: behavior() });
    } else if (dir < 0 && position() <= 4) {
      track.scrollTo({ left: sign() * max, behavior: behavior() });
    } else {
      const delta = dir * sign() * cardStep();
      // Organise Your Space (resumable rows): quick 0.32s glide to the next card, then a pause (merchant: "rok rok ke par tez").
      if (autoplay && !reduceMotion.matches) {
        const from = track.scrollLeft;
        const t0 = performance.now();
        track.style.scrollSnapType = 'none';
        track.style.scrollBehavior = 'auto';
        const tick = (now) => {
          const p = Math.min(1, (now - t0) / 320);
          track.scrollLeft = from + delta * (1 - Math.pow(1 - p, 3));
          if (p < 1) requestAnimationFrame(tick);
          else { track.style.scrollSnapType = ''; track.style.scrollBehavior = ''; }
        };
        requestAnimationFrame(tick);
      } else {
        track.scrollBy({ left: delta, behavior: behavior() });
      }
    }
  };

  const stop = () => {
    clearInterval(timer);
    timer = null;
  };

  const start = () => {
    stop();
    if (!autoplay || stopped || hovering || !visible || document.hidden) return;
    timer = setInterval(() => go(1), resumable ? 1500 : STEP_DELAY);
  };

  // Merchant, 19 Oct 2026: once a shopper touches, drags, scrolls or uses an arrow, auto slide stops for good on that row.
  // Rows with [data-autoscroll-resume] (Organise Your Space) only pause: they start moving again 2 seconds after the
  // finger lifts or the last scroll / arrow tap. Hovering with a mouse only pauses it while the pointer is over the row.
  const resumable = track.hasAttribute('data-autoscroll-resume');
  let stopped = false;
  let hovering = false;
  const scheduleResume = () => {
    clearTimeout(resumeTimer);
    resumeTimer = setTimeout(() => { if (!held) start(); }, RESUME_DELAY);
  };
  const hold = () => {
    held = true;
    clearTimeout(resumeTimer);
    stop();
    if (!resumable) stopped = true;
  };
  const release = () => {
    held = false;
    if (resumable) scheduleResume();
  };
  const nudge = () => {
    stop();
    if (resumable) scheduleResume();
    else stopped = true;
  };

  track.addEventListener('pointerdown', hold);
  track.addEventListener('wheel', nudge, { passive: true });
  track.addEventListener('mouseenter', () => { hovering = true; stop(); });
  track.addEventListener('mouseleave', () => { hovering = false; start(); });
  track.addEventListener('touchstart', hold, { passive: true });
  track.addEventListener('pointerup', release);
  track.addEventListener('pointercancel', release);
  track.addEventListener('touchend', release, { passive: true });
  track.addEventListener('touchcancel', release, { passive: true });

  enableDrag(track, cardStep, behavior);

  const section = track.closest('section') || track.parentElement;
  section?.querySelector('[data-row-prev]')?.addEventListener('click', () => {
    nudge();
    go(-1);
  });
  section?.querySelector('[data-row-next]')?.addEventListener('click', () => {
    nudge();
    go(1);
  });

  if (autoplay) {
    new IntersectionObserver((entries) => {
      visible = entries.some((entry) => entry.isIntersecting);
      visible ? start() : stop();
    }, { threshold: 0.2 }).observe(track);
    document.addEventListener('visibilitychange', () => (document.hidden ? stop() : start()));
  }
}

// Every carousel can be grabbed with the mouse and dragged sideways (phones swipe natively).
// Releasing glides to the nearest card. A real drag cancels the click so a card link doesn't open.
function enableDrag(track, cardStep, behavior) {
  if (track.dataset.dragReady) return;
  track.dataset.dragReady = 'true';
  {
    let dragging = false;
    let moved = false;
    let startX = 0;
    let startLeft = 0;
    track.style.cursor = 'grab';
    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      dragging = true;
      moved = false;
      startX = event.clientX;
      startLeft = track.scrollLeft;
    });
    window.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      const dx = event.clientX - startX;
      if (!moved && Math.abs(dx) > 5) {
        moved = true;
        track.style.scrollSnapType = 'none';
        track.style.scrollBehavior = 'auto';
        track.style.cursor = 'grabbing';
      }
      if (moved) track.scrollLeft = startLeft - dx;
    });
    window.addEventListener('pointerup', () => {
      if (!dragging) return;
      dragging = false;
      track.style.cursor = 'grab';
      if (!moved) return;
      const step = cardStep();
      const target = Math.round(track.scrollLeft / step) * step;
      track.style.scrollBehavior = '';
      track.scrollTo({ left: target, behavior: behavior() });
      setTimeout(() => { track.style.scrollSnapType = ''; }, 450);
    });
    track.addEventListener('click', (event) => {
      if (moved) {
        event.preventDefault();
        event.stopPropagation();
        moved = false;
      }
    }, true);
    track.addEventListener('dragstart', (event) => event.preventDefault());

    // Mouse wheel / trackpad: scrolling over a carousel slides it sideways. Once it reaches
    // the first or last card, the page scrolls normally again. Settles on the nearest card.
    let settleTimer = null;
    track.addEventListener('wheel', (event) => {
      if (event.ctrlKey) return;
      const max = track.scrollWidth - track.clientWidth;
      if (max <= 4) return;
      const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
      const unit = event.deltaMode === 1 ? 40 : event.deltaMode === 2 ? track.clientWidth : 1;
      const pos = Math.abs(track.scrollLeft);
      const canMove = (delta > 0 && pos < max - 2) || (delta < 0 && pos > 2);
      if (!canMove) return;
      event.preventDefault();
      track.style.scrollSnapType = 'none';
      track.style.scrollBehavior = 'auto';
      track.scrollLeft += delta * unit;
      clearTimeout(settleTimer);
      settleTimer = setTimeout(() => {
        const step = cardStep();
        track.style.scrollBehavior = '';
        track.scrollTo({ left: Math.round(track.scrollLeft / step) * step, behavior: behavior() });
        setTimeout(() => { track.style.scrollSnapType = ''; }, 450);
      }, 160);
    }, { passive: false });
  }
}

// Carousels built with the s-carousel block ([data-carousel-track]) get the same mouse drag.
function setupBlockCarousel(track) {
  const behavior = () => (reduceMotion.matches ? 'auto' : 'smooth');
  const cardStep = () => {
    const first = track.children[0];
    if (!first) return track.clientWidth;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    return first.getBoundingClientRect().width + gap;
  };
  enableDrag(track, cardStep, behavior);
}

// Touch swipe backup: phones normally swipe these tracks natively. If a device or preview
// doesn't move the track on a sideways swipe, this moves it with the finger and then glides
// to the nearest card. When native swiping works, scrollLeft changes on its own and this stays idle.
function enableTouchSwipe(track) {
  if (track.dataset.touchReady) return;
  track.dataset.touchReady = 'true';
  let startX = 0;
  let startY = 0;
  let startLeft = 0;
  let mode = null; // null = undecided, 'native', 'manual', 'vertical'

  const step = () => {
    const first = track.children[0];
    if (!first) return track.clientWidth;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    return first.getBoundingClientRect().width + gap;
  };

  track.addEventListener('touchstart', (event) => {
    if (event.touches.length !== 1) return;
    if (track.scrollWidth - track.clientWidth <= 4) { mode = 'vertical'; return; }
    startX = event.touches[0].clientX;
    startY = event.touches[0].clientY;
    startLeft = track.scrollLeft;
    mode = null;
  }, { passive: true });

  track.addEventListener('touchmove', (event) => {
    if (event.touches.length !== 1 || mode === 'native' || mode === 'vertical') return;
    const dx = event.touches[0].clientX - startX;
    const dy = event.touches[0].clientY - startY;
    if (mode === null) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      if (Math.abs(dy) > Math.abs(dx)) {
        mode = 'vertical';
        return;
      }
      if (!event.cancelable || Math.abs(track.scrollLeft - startLeft) > 1) {
        mode = 'native';
        return;
      }
      mode = 'manual';
      track.style.scrollSnapType = 'none';
      track.style.scrollBehavior = 'auto';
    }
    if (mode === 'manual') {
      if (event.cancelable) event.preventDefault();
      track.scrollLeft = startLeft - dx;
    }
  }, { passive: false });

  const finish = () => {
    if (mode !== 'manual') {
      mode = null;
      return;
    }
    mode = null;
    const s = step();
    track.style.scrollBehavior = '';
    track.scrollTo({ left: Math.round(track.scrollLeft / s) * s, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    setTimeout(() => { track.style.scrollSnapType = ''; }, 450);
  };
  track.addEventListener('touchend', finish, { passive: true });
  track.addEventListener('touchcancel', finish, { passive: true });
}

const init = () => {
  document.querySelectorAll('[data-carousel]').forEach(setup);
  document.querySelectorAll('[data-carousel-track]').forEach(setupBlockCarousel);
  // 20 Oct 2026: manual touch fallback disabled (it took over the first finger move and killed the phone's native smooth/momentum swipe). Native scrolling handles touch.
  // document.querySelectorAll('[data-carousel], [data-carousel-track], [data-slideshow-track]').forEach(enableTouchSwipe);
};
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
document.addEventListener('shopify:section:load', init);
document.addEventListener('shopify:block:select', init);
